import React from 'react';
import Link from 'next/link';

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const URL_REGEX = /https?:\/\/[^\s<>"']+/g;
const TRAILING_PUNCTUATION_REGEX = /[),.!?;:]+$/;

/**
 * How far past the requested length truncateAtSafeBoundary may run to keep a straddling
 * link whole. Generous enough for any realistic URL (an own-host event link is ~60
 * characters), small enough that one pathological URL cannot turn a preview into the whole
 * post.
 */
export const DEFAULT_LINK_OVERSHOOT = 200;

/**
 * How much of a partial word may be discarded to reach the word boundary before it. Longer
 * than any real word in either supported language, so the fallback only engages for an
 * unbroken run of characters that is not a word at all -- where honouring the boundary
 * would gut the preview instead of tidying it.
 */
export const DEFAULT_WORD_SNAPBACK = 40;

// Static allowlist of own-app hosts (hostname[:port]). Kept module-level and
// SSR/CSR-deterministic so 'use client' components don't hydrate with
// different markup than their SSR pass produced. Compared against URL.host
// (not hostname) so same-hostname-different-port URLs are NOT misrouted.
const OWN_HOSTS: ReadonlySet<string> = new Set([
  'www.togetherwe5b.org',
  'app.togetherwe5b.org', // legacy prod host — kept during the www migration
  'dev.app.togetherwe5b.org',
  'localhost:3000',
  '127.0.0.1:3000',
]);

function isAllowedUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch { // eslint-disable-line no-restricted-syntax -- URL validation in user content
    return false;
  }
}

function parseInternal(url: string): { isInternal: boolean; relativeHref: string } {
  try {
    const parsed = new URL(url);
    if (OWN_HOSTS.has(parsed.host)) {
      return { isInternal: true, relativeHref: `${parsed.pathname}${parsed.search}${parsed.hash}` };
    }
    return { isInternal: false, relativeHref: '' };
  } catch { // eslint-disable-line no-restricted-syntax -- URL validation in user content
    return { isInternal: false, relativeHref: '' };
  }
}

function splitTrailingPunctuation(url: string): { cleanUrl: string; trailing: string } {
  const match = url.match(TRAILING_PUNCTUATION_REGEX);
  if (!match) return { cleanUrl: url, trailing: '' };
  const trailing = match[0];
  const cleanUrl = url.slice(0, -trailing.length);
  return { cleanUrl, trailing };
}

/**
 * A run of source text that renders as a link (or, when the URL is rejected, as the literal
 * text of one). `start`/`end` index into the ORIGINAL string, so a caller can reason about
 * cut points without re-parsing.
 */
interface LinkSpan {
  start: number;
  end: number;
  /** The exact source text covered, including any trailing punctuation for a bare URL. */
  raw: string;
  kind: 'markdown' | 'url';
  /** Markdown only: the bracketed label and the parenthesised target. */
  label?: string;
  rawUrl?: string;
}

function findUrlSpans(text: string, from: number, to: number): LinkSpan[] {
  const slice = text.slice(from, to);
  const spans: LinkSpan[] = [];
  let match: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(slice)) !== null) {
    spans.push({
      start: from + match.index,
      end: from + match.index + match[0].length,
      raw: match[0],
      kind: 'url',
    });
  }

  return spans;
}

/**
 * Locate every linkable span, in source order and without overlaps.
 *
 * This is the SINGLE definition of "where the links are", consumed by both linkifyText (to
 * build nodes) and truncateWithoutSplittingLinks (to pick a safe cut). Two independent
 * scanners would be free to drift apart, and drift here is exactly the defect this function
 * was extracted to close: a cut made by one scanner's rules and linkified by the other's
 * produces an anchor pointing at a URL nobody wrote.
 *
 * Markdown links are matched across the whole string first and bare URLs only in the gaps
 * between them, so a URL inside a `[label](target)` construct is never also matched on its
 * own. That two-pass order is load-bearing, not incidental.
 */
function findLinkSpans(text: string): LinkSpan[] {
  const markdownSpans: LinkSpan[] = [];
  let match: RegExpExecArray | null;

  MARKDOWN_LINK_REGEX.lastIndex = 0;
  while ((match = MARKDOWN_LINK_REGEX.exec(text)) !== null) {
    markdownSpans.push({
      start: match.index,
      end: match.index + match[0].length,
      raw: match[0],
      kind: 'markdown',
      label: match[1],
      rawUrl: match[2],
    });
  }

  const spans: LinkSpan[] = [];
  let cursor = 0;

  for (const markdown of markdownSpans) {
    spans.push(...findUrlSpans(text, cursor, markdown.start));
    spans.push(markdown);
    cursor = markdown.end;
  }
  spans.push(...findUrlSpans(text, cursor, text.length));

  return spans;
}

function renderMarkdownSpan(span: LinkSpan, key: string): React.ReactNode[] {
  const rawUrl = span.rawUrl ?? '';
  if (!isAllowedUrl(rawUrl)) return [span.raw];

  const { isInternal, relativeHref } = parseInternal(rawUrl);
  if (isInternal) {
    return [
      <Link key={key} href={relativeHref} className="text-primary underline underline-offset-2 break-all">
        {span.label}
      </Link>,
    ];
  }

  return [
    <a
      key={key}
      href={rawUrl}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="text-primary underline underline-offset-2 break-all"
    >
      {span.label}
    </a>,
  ];
}

function renderUrlSpan(span: LinkSpan, key: string): React.ReactNode[] {
  const { cleanUrl, trailing } = splitTrailingPunctuation(span.raw);
  if (!cleanUrl || !isAllowedUrl(cleanUrl)) return [span.raw];

  const { isInternal, relativeHref } = parseInternal(cleanUrl);
  const anchor = isInternal ? (
    <Link key={key} href={relativeHref} className="text-primary underline underline-offset-2 break-all">
      {cleanUrl}
    </Link>
  ) : (
    <a
      key={key}
      href={cleanUrl}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="text-primary underline underline-offset-2 break-all"
    >
      {cleanUrl}
    </a>
  );

  return trailing ? [anchor, trailing] : [anchor];
}

export function linkifyText(text: string): React.ReactNode[] {
  if (!text) return [];

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  findLinkSpans(text).forEach((span, index) => {
    if (span.start > cursor) nodes.push(text.slice(cursor, span.start));

    const key = `${span.kind}-${index}`;
    nodes.push(
      ...(span.kind === 'markdown' ? renderMarkdownSpan(span, key) : renderUrlSpan(span, key)),
    );

    cursor = span.end;
  });

  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
}

export interface TruncatedPreview {
  /** The preview text. Never ends part-way through a word or a link. */
  text: string;
  /** True only when `text` is genuinely shorter than the source. */
  truncated: boolean;
}

export interface SafeBoundaryOptions {
  /** Characters the preview may run over `maxLength` to keep a straddling link whole. */
  linkOvershoot?: number;
  /** Characters of a partial word the preview may give up to reach the boundary before it. */
  wordSnapback?: number;
}

const WHITESPACE = /\s/;

/** True when `index` has a non-space character on both sides, i.e. it is inside a word. */
function splitsAWord(text: string, index: number): boolean {
  if (index <= 0 || index >= text.length) return false;
  return !WHITESPACE.test(text[index - 1]) && !WHITESPACE.test(text[index]);
}

function lastWhitespaceBefore(text: string, index: number): number {
  for (let i = index - 1; i >= 0; i -= 1) {
    if (WHITESPACE.test(text[i])) return i;
  }
  return -1;
}

/**
 * Shorten `text` for a preview, treating `maxLength` as a target rather than a hard stop so
 * the cut can land on a natural boundary instead of part-way through a word or a link.
 *
 * Words and links are both kept whole, but for different reasons, so they move the cut in
 * different directions:
 *
 *   - A partial WORD is only untidy, and expanding the post brings it straight back, so the
 *     cut moves BACK to the boundary before it and the fragment is simply dropped. An
 *     unbroken run longer than `wordSnapback` is not a word; there the cut stands, since
 *     honouring it would leave almost nothing to preview.
 *   - A partial LINK is not merely untidy. Callers hand this result straight to linkifyText,
 *     which cannot tell a whole URL from a beheaded one, so half of
 *     `https://www.togetherwe5b.org/events/<24-char-id>` still renders as a real, clickable
 *     link -- to an event that does not exist, or, for an off-site address, to a different
 *     real page with no error at all. Losing the link also costs the reader the destination,
 *     so the cut moves FORWARD past the end of the link and keeps it usable while collapsed.
 *     Only a link longer than `linkOvershoot` is dropped instead, back at its start.
 *
 * `truncated` reports whether anything was actually withheld, computed from the final cut
 * rather than from `text.length > maxLength` -- running forward over a trailing link can
 * consume the rest of the string, and a "see more" control with nothing left to reveal is a
 * dead end.
 */
export function truncateAtSafeBoundary(
  text: string,
  maxLength: number,
  options: SafeBoundaryOptions = {},
): TruncatedPreview {
  if (!text || text.length <= maxLength) return { text, truncated: false };

  const linkOvershoot = options.linkOvershoot ?? DEFAULT_LINK_OVERSHOOT;
  const wordSnapback = options.wordSnapback ?? DEFAULT_WORD_SNAPBACK;

  const spans = findLinkSpans(text);
  // Strictly inside: a cut landing exactly on either boundary already leaves the link whole
  // or omits it entirely, so it needs no adjustment.
  const enclosingLink = (index: number) => spans.find((span) => span.start < index && index < span.end);

  let cut = Math.max(0, maxLength);
  const straddledLink = enclosingLink(cut);

  if (straddledLink) {
    cut = straddledLink.end <= cut + linkOvershoot ? straddledLink.end : straddledLink.start;
  } else if (splitsAWord(text, cut)) {
    const boundary = lastWhitespaceBefore(text, cut);
    if (boundary >= 0 && cut - boundary <= wordSnapback) {
      cut = boundary;
      // A markdown label may contain spaces, so moving back to a word boundary can land
      // inside an earlier link. Drop that link rather than bisect it.
      const reentered = enclosingLink(cut);
      if (reentered) cut = reentered.start;
    }
  }

  if (cut >= text.length) return { text, truncated: false };
  return { text: text.slice(0, cut).trimEnd(), truncated: true };
}
